/**
 * Twilio Configuration React Query Hooks
 * 
 * Provides React Query hooks for Twilio configuration operations with caching,
 * optimistic updates, and automatic refetching.
 */

import { useQuery, useMutation, useQueryClient, UseQueryOptions } from '@tanstack/react-query';
import { 
  twilioConfigService, 
  type UpsertTwilioConfigInput 
} from '../services/TwilioConfigService';
import type { TwilioConfig } from '../repositories/TwilioConfigRepository';

/**
 * Query keys for Twilio config queries
 */
export const twilioConfigKeys = {
  all: ['twilioConfig'] as const,
  byCompany: (companyId: string) => [...twilioConfigKeys.all, companyId] as const,
};

/**
 * Hook to fetch Twilio config for a company
 */
export function useTwilioConfig(
  companyId: string,
  options?: UseQueryOptions<TwilioConfig | null, Error>
) {
  return useQuery<TwilioConfig | null, Error>({
    queryKey: twilioConfigKeys.byCompany(companyId),
    queryFn: () => twilioConfigService.getConfig(companyId),
    staleTime: 10 * 60 * 1000, // 10 minutes
    enabled: !!companyId,
    ...options,
  });
}

/**
 * Hook to upsert Twilio config
 */
export function useUpsertTwilioConfig() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ companyId, input }: { companyId: string; input: UpsertTwilioConfigInput }) => 
      twilioConfigService.upsertConfig(companyId, input),
    onSuccess: (updatedConfig) => {
      queryClient.setQueryData(
        twilioConfigKeys.byCompany(updatedConfig.company_id),
        updatedConfig
      );
    },
  });
}

/**
 * Hook to update auth token
 */
export function useUpdateAuthToken() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ companyId, token }: { companyId: string; token: string }) =>
      twilioConfigService.updateAuthToken(companyId, token),
    onSuccess: (updatedConfig) => {
      queryClient.setQueryData(
        twilioConfigKeys.byCompany(updatedConfig.company_id),
        updatedConfig
      );
    },
  });
}

/**
 * Hook to toggle recording
 */
export function useToggleRecording() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (companyId: string) => twilioConfigService.toggleRecording(companyId),
    onMutate: async (companyId) => {
      await queryClient.cancelQueries({ queryKey: twilioConfigKeys.byCompany(companyId) });
      
      const previousConfig = queryClient.getQueryData<TwilioConfig>(
        twilioConfigKeys.byCompany(companyId)
      );
      
      if (previousConfig) {
        queryClient.setQueryData<TwilioConfig>(
          twilioConfigKeys.byCompany(companyId),
          {
            ...previousConfig,
            recording_enabled: !previousConfig.recording_enabled,
          }
        );
      }
      
      return { previousConfig };
    },
    onError: (err, companyId, context) => {
      if (context?.previousConfig) {
        queryClient.setQueryData(
          twilioConfigKeys.byCompany(companyId),
          context.previousConfig
        );
      }
    },
    onSuccess: (updatedConfig) => {
      queryClient.setQueryData(
        twilioConfigKeys.byCompany(updatedConfig.company_id),
        updatedConfig
      );
    },
  });
}

/**
 * Hook to toggle transcription
 */
export function useToggleTranscription() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (companyId: string) => twilioConfigService.toggleTranscription(companyId),
    onMutate: async (companyId) => {
      await queryClient.cancelQueries({ queryKey: twilioConfigKeys.byCompany(companyId) });
      
      const previousConfig = queryClient.getQueryData<TwilioConfig>(
        twilioConfigKeys.byCompany(companyId)
      );
      
      if (previousConfig) {
        queryClient.setQueryData<TwilioConfig>(
          twilioConfigKeys.byCompany(companyId),
          {
            ...previousConfig,
            transcription_enabled: !previousConfig.transcription_enabled,
          }
        );
      }
      
      return { previousConfig };
    },
    onError: (err, companyId, context) => {
      if (context?.previousConfig) {
        queryClient.setQueryData(
          twilioConfigKeys.byCompany(companyId),
          context.previousConfig
        );
      }
    },
    onSuccess: (updatedConfig) => {
      queryClient.setQueryData(
        twilioConfigKeys.byCompany(updatedConfig.company_id),
        updatedConfig
      );
    },
  });
}

/**
 * Hook to update transcription provider
 */
export function useUpdateTranscriptionProvider() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ companyId, provider }: { companyId: string; provider: 'deepgram' | 'openai' | 'google' }) =>
      twilioConfigService.updateTranscriptionProvider(companyId, provider),
    onSuccess: (updatedConfig) => {
      queryClient.setQueryData(
        twilioConfigKeys.byCompany(updatedConfig.company_id),
        updatedConfig
      );
    },
  });
}

/**
 * Hook to update phone number
 */
export function useUpdatePhoneNumber() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ companyId, phoneNumber }: { companyId: string; phoneNumber: string }) =>
      twilioConfigService.updatePhoneNumber(companyId, phoneNumber),
    onSuccess: (updatedConfig) => {
      queryClient.setQueryData(
        twilioConfigKeys.byCompany(updatedConfig.company_id),
        updatedConfig
      );
    },
  });
}
